import { Stack, Typography } from '@mui/material';
import { useCallback, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { Callout } from '@/components/notifications/Callout/Callout';
import { NON_SELECTABLE_EXISTING_DOCUMENT_TYPES } from '@/modules/documents/documentTypePolicies';
import { useMultiDocumentUploaderContext } from '@/modules/documents/MultiDocumentUploader/context/multiDocumentUploader.context';
import { MultiDocumentUploaderProvider } from '@/modules/documents/MultiDocumentUploader/context/MultiDocumentUploader.provider';
import { useDocumentTypesValidation } from '@/modules/documents/MultiDocumentUploader/hooks/useDocumentTypesValidation';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { ROUTE_KEYS } from '@/navigation/constants';
import { COLORS } from '@/styles/tokens/colors';
import { DocumentType, WorkflowTrackerKind } from '@/types/schema';

import {
  ClientOnboardingDeliverableModalBodyProps,
  ClientOnboardingDeliverableStep,
} from '../../types';
import { DocumentProcessingCompletionButton } from '../components/DocumentProcessingCompletionButton';
import { DocumentProcessingStatus } from '../components/DocumentProcessingStatus';
import { DocumentUploadWithFrame } from '../components/DocumentUploadWithFrame';
import { usePreselectHouseholdDocuments } from '../hooks/usePreselectHouseholdDocuments';
import { useGenerateEstateSnapshotMutation } from './graphql/GenerateEstateSnapshot.generated';

function GenerateEstateSnapshotModalBodyInner({
  onChildPhaseChange,
  onClose,
  preselectedExistingDocumentIds,
}: ClientOnboardingDeliverableModalBodyProps) {
  const { householdId } = useHouseholdDetailsContext();
  const { navigate } = useNavigateToRoute();
  const { uploadedFiles, uploadDocuments, selectedExistingDocuments } =
    useMultiDocumentUploaderContext();
  const allDocumentTypesSelected = useDocumentTypesValidation();

  const [uploadedDocumentIds, setUploadedDocumentIds] = useState<string[]>([]);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [phase, setPhase] = useState<ClientOnboardingDeliverableStep>(
    ClientOnboardingDeliverableStep.UPLOAD
  );

  const [generateEstateSnapshot, { loading: generatingSnapshot }] =
    useGenerateEstateSnapshotMutation();

  // By default, we expect that users will want to create an estate overview with all of their household documents,
  // so we preselect all household documents by default, and they can deselect any they don't want to include.
  usePreselectHouseholdDocuments({
    householdId,
    preselectedExistingDocumentIds,
    excludedDocumentTypes: NON_SELECTABLE_EXISTING_DOCUMENT_TYPES,
  });

  const handlePhaseChange = useCallback(
    (newPhase: ClientOnboardingDeliverableStep) => {
      setPhase(newPhase);
      onChildPhaseChange?.(newPhase);
    },
    [onChildPhaseChange]
  );

  const handleGenerate = useCallback(async () => {
    if (!householdId) {
      return;
    }

    try {
      setErrorMessage(null);

      let newlyUploadedDocumentIds: string[] = [];

      // Upload documents if requested
      if (uploadedFiles.length > 0) {
        setUploadingDocuments(true);
        const ids = await uploadDocuments({
          householdId,
          defaultDocumentType: DocumentType.Other,
        });
        newlyUploadedDocumentIds = ids;
        setUploadedDocumentIds(ids);
        setUploadingDocuments(false);
      }

      // Combine newly uploaded documents with existing selected documents
      const allDocumentIds = [
        ...newlyUploadedDocumentIds,
        ...selectedExistingDocuments.map((doc) => doc.id),
      ];

      // Generate estate overview
      await generateEstateSnapshot({
        variables: { householdID: householdId, documentIDs: allDocumentIds },
      });

      handlePhaseChange(ClientOnboardingDeliverableStep.PROCESSING);
    } catch {
      setUploadingDocuments(false);
      setErrorMessage(
        'We failed to generate an estate overview. Please check your documents (if any) and try again.'
      );
    }
  }, [
    householdId,
    uploadedFiles.length,
    selectedExistingDocuments,
    generateEstateSnapshot,
    handlePhaseChange,
    uploadDocuments,
  ]);

  if (!householdId) {
    return null;
  }

  const uploadedDocumentsCount = uploadedFiles.length;
  const selectedExistingDocumentsCount = selectedExistingDocuments.length;
  const relevantDocumentsCount =
    uploadedDocumentsCount + selectedExistingDocumentsCount;
  const documentNoun = relevantDocumentsCount === 1 ? 'document' : 'documents';

  const header = (() => {
    // If no documents are uploaded, we don't show this
    if (uploadedDocumentsCount === 0) return '';
    // If no documents are selected, we show the number of uploaded documents
    if (selectedExistingDocumentsCount === 0) {
      return `${uploadedDocumentsCount} ${documentNoun} uploaded`;
    }
    // If both are present, we show the number of relevant documents
    return `${relevantDocumentsCount} ${documentNoun} uploaded and selected`;
  })();

  const renderUploadPhase = () => {
    const calloutElement = (
      <Callout severity="info-high">
        The Estate360 presentation is not connected to the client&apos;s
        underlying data. Any updates made to the presentation will not be
        reflected anywhere else in the client profile and any updates made to
        the underlying client data will not be reflected in the estate overview.
      </Callout>
    );

    return (
      <Stack spacing={2}>
        {uploadedDocumentsCount === 0 && (
          <Stack direction="column" gap={0.5}>
            <Typography variant="title-l" color={COLORS.NAVY['700']}>
              Create with document(s)
            </Typography>
            <Typography variant="body-s" color={COLORS.GRAY['500']}>
              Luminary AI will look at these documents and generate an Estate360
              presentation.
            </Typography>
          </Stack>
        )}

        <Stack spacing={1}>
          {uploadedDocumentsCount === 0 && calloutElement}
          <DocumentUploadWithFrame
            allowSelectExistingDocuments
            excludedExistingDocumentTypes={
              NON_SELECTABLE_EXISTING_DOCUMENT_TYPES
            }
            householdId={householdId}
            hideHeader={uploadedDocumentsCount === 0}
            header={header}
            subheader="Luminary AI will process each document and generate an estate overview for you to review."
            errorMessage={errorMessage}
            onClearError={() => setErrorMessage(null)}
            additionalContentAboveUploader={
              uploadedDocumentsCount > 0 ? calloutElement : undefined
            }
          />

          {uploadedDocumentsCount + selectedExistingDocumentsCount > 0 && (
            <Button
              variant="primary"
              size="sm"
              fullWidth
              onClick={() => handleGenerate()}
              loading={uploadingDocuments || generatingSnapshot}
              disabled={!allDocumentTypesSelected}
            >
              Generate Estate360 presentation
            </Button>
          )}
        </Stack>
      </Stack>
    );
  };

  const renderProcessingOrReviewPhase = () => {
    const isReview = phase === ClientOnboardingDeliverableStep.REVIEW;

    const header = isReview
      ? 'Success! Estate360 presentation ready'
      : `Processing ${relevantDocumentsCount ? documentNoun : 'request'}...`;

    const subheader = isReview ? (
      'Your Estate360 presentation has been prepared for you.'
    ) : (
      <>
        We are generating your Estate360 presentation from the selected{' '}
        {documentNoun}. Once created, you can view it in the Presentations tab.
        <strong> This may take up to 15 minutes.</strong>
      </>
    );

    return (
      <Stack spacing={2}>
        {/* Show DocumentProcessingStatus if we have uploaded docs or we're still processing */}
        {(phase === ClientOnboardingDeliverableStep.PROCESSING ||
          uploadedDocumentIds.length > 0) && (
          <DocumentProcessingStatus
            uploadedDocumentIds={uploadedDocumentIds}
            workflowTrackerKind={WorkflowTrackerKind.EstateSnapshotGeneration}
            householdId={householdId}
            header={header}
            subheader={subheader}
            onAllProcessed={() =>
              handlePhaseChange(ClientOnboardingDeliverableStep.REVIEW)
            }
          />
        )}

        {/* When no docs were uploaded and we're in REVIEW, show simple success UI */}
        {uploadedDocumentIds.length === 0 && isReview && (
          <Stack spacing={2}>
            <Stack spacing={1}>
              <Typography variant="title-xl">{header}</Typography>
              <Typography variant="body-xs">{subheader}</Typography>
            </Stack>
          </Stack>
        )}

        <Stack spacing={2}>
          <DocumentProcessingCompletionButton
            completionButtonLabel="Go to presentations"
            onCompletionClick={() => {
              onClose();
              navigate(ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATIONS, {
                householdId,
              });
            }}
          />
          {phase === ClientOnboardingDeliverableStep.PROCESSING && onClose && (
            <Typography variant="body-xs" textAlign="center">
              Progress saved in background — you can safely{' '}
              <Button
                variant="transparent"
                size="sm"
                onClick={onClose}
                sx={{
                  textDecoration: 'underline',
                  p: 0,
                  minWidth: 'auto',
                  verticalAlign: 'baseline',
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                }}
              >
                close this window
              </Button>
            </Typography>
          )}
        </Stack>
      </Stack>
    );
  };

  return (
    <Stack
      padding={4}
      sx={{
        maxWidth: 640,
        margin: '0 auto',
        minHeight: '100%',
        justifyContent: 'center',
      }}
    >
      {phase === ClientOnboardingDeliverableStep.UPLOAD && renderUploadPhase()}
      {(phase === ClientOnboardingDeliverableStep.PROCESSING ||
        phase === ClientOnboardingDeliverableStep.REVIEW) &&
        renderProcessingOrReviewPhase()}
    </Stack>
  );
}

export function GenerateEstateSnapshotModalBody(
  props: ClientOnboardingDeliverableModalBodyProps
) {
  return (
    <MultiDocumentUploaderProvider>
      <GenerateEstateSnapshotModalBodyInner {...props} />
    </MultiDocumentUploaderProvider>
  );
}
